<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useSessionStorage } from '@vueuse/core'
import { loginConfig as rocnovelConfig } from './configs/rocnovel'
import { loginConfig as ocereadConfig } from './configs/oceread'
import { loginConfig as talewaveConfig } from './configs/talewave'
import { loginConfig as ficmoonConfig } from './configs/ficmoon'
import { loginConfig as novelarcConfig } from './configs/novelarc'
import { loginConfig as storyellaConfig } from './configs/storyella'
import { loginConfig as florastoryConfig } from './configs/florastory'
import { loginConfig as silktaleConfig } from './configs/silktale'
import { loginConfig as pearlaConfig } from './configs/pearla'
import { loginConfig as fictoraConfig } from './configs/fictora'
import { loginConfig as marinovelConfig } from './configs/marinovel'
import { loginConfig as flipnovelConfig } from './configs/flipnovel'
import { loginConfig as moonpageConfig } from './configs/moonpage'
import { loginConfig as storlynConfig } from './configs/storlyn'
import { loginConfig as novarisConfig } from './configs/novaris'
import LoginPageDefault from './components/LoginPage.default.vue'
import LoginPageFlipnovel from './components/LoginPage.flipnovel.vue'
import { useHistoryStore } from '@/stores'
import { useTripartiteLogin } from '@/composables/useTripartiteLogin'
import { useEmailLogin } from '@/composables/useEmailLogin'
import { directPageViewTrack, useElementClick } from '@/composables/tracks'
import { loadBrandComponent, loadBrandConfig } from '@/utils/loadBrandConfig'
import { LOCALE_MAP, type LocaleCode, setLocale } from '@/i18n'

const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  talewave: talewaveConfig,
  ficmoon: ficmoonConfig,
  novelarc: novelarcConfig,
  storyella: storyellaConfig,
  florastory: florastoryConfig,
  silktale: silktaleConfig,
  pearla: pearlaConfig,
  fictora: fictoraConfig,
  marinovel: marinovelConfig,
  flipnovel: flipnovelConfig,
  moonpage: moonpageConfig,
  storlyn: storlynConfig,
  novaris: novarisConfig,
})

const LoginPage = loadBrandComponent({
  rocnovel: LoginPageDefault,
  oceread: LoginPageDefault,
  talewave: LoginPageDefault,
  ficmoon: LoginPageDefault,
  novelarc: LoginPageDefault,
  storyella: LoginPageDefault,
  florastory: LoginPageDefault,
  silktale: LoginPageDefault,
  pearla: LoginPageDefault,
  fictora: LoginPageDefault,
  marinovel: LoginPageDefault,
  flipnovel: LoginPageFlipnovel,
  storlyn: LoginPageFlipnovel,
  novaris: LoginPageFlipnovel,
})

const route = useRoute()
const router = useRouter()
const historyStore = useHistoryStore()
const showLogout = localStorage.getItem('logout')
const deviceUuid = useSessionStorage<any>('did', undefined)
const redirect = useSessionStorage<any>('redirect', undefined)
const loginToken = useSessionStorage<any>('loginToken', undefined)
const { elementClickForLogin } = useElementClick()

const redirectUrl = route.query.redirect as string
const d = route.query.d as string
const t = route.query.t as string
const from = (route.query.from || 'Profile') as string
const lang = route.query.lang as string | undefined

const init = function () {
  // If host passes lang, honor it first (and persist)
  if (lang && (lang in LOCALE_MAP))
    setLocale(lang as LocaleCode)

  if (redirectUrl)
    redirect.value = redirectUrl

  const query = { ...route.query }
  if (d) {
    deviceUuid.value = d
    delete query.d
  }

  if (t) {
    loginToken.value = t
    delete query.t
  }

  // Clean up transient query params
  if (lang)
    delete query.lang

  router.replace({
    query: { ...query },
  })

  directPageViewTrack('Login', from)
}
init()

const availableLogins = computed(() => {
  try {
    const appConfig = localStorage.getItem('AppConfig')
    if (!appConfig)
      return []
    const configData = JSON.parse(appConfig)
    return configData.login || []
  }
  catch {
    return []
  }
})

if (availableLogins.value.length === 0 && !route.query.emailLogin) {
  router.replace({
    query: {
      ...route.query,
      emailLogin: '1',
    },
  })
}

const { skeleton, signInWithGoogle, signInWithFacebook, signInWithApple, logout } = useTripartiteLogin(deviceUuid.value, loginToken.value, redirect.value)

const isEmailLogin = computed({
  get: () => ~~route.query.emailLogin,
  set: (v: any) => (route.query.emailLogin = `${v}`),
})

function setEmailLogin() {
  elementClickForLogin('login_email', {
    route_title: from,
    route_host: new URL(redirect.value).host,
    route_url: redirect.value,
  })
  historyStore.$push(window.location.href)
  router.push({
    query: {
      ...route.query,
      emailLogin: '1',
    },
  })
}

const back = function () {
  window.history.go(-1)
}

const { form, getCode, beforeSignIn, resetEmail } = useEmailLogin(deviceUuid.value, loginToken.value, redirect.value)

function updateFlipnovelFormField(field: 'email' | 'code', value: string) {
  form[field] = value
}

watch(
  () => isEmailLogin.value,
  () => resetEmail(),
)

const userAgreementUrl = import.meta.env.VITE_USER_AGREEMENT_URL
const privacyPolicyUrl = import.meta.env.VITE_PRIVACY_POLICY_URL
const loginBgUrl = `url(${config.resources.loginBg})`
</script>

<template>
  <LoginPage
    :config="config"
    :skeleton="skeleton"
    :is-email-login="isEmailLogin"
    :available-logins="availableLogins"
    :show-logout="showLogout"
    :form="form"
    :user-agreement-url="userAgreementUrl"
    :privacy-policy-url="privacyPolicyUrl"
    :login-bg-url="loginBgUrl"
    :sign-in-with-facebook="signInWithFacebook"
    :sign-in-with-google="signInWithGoogle"
    :sign-in-with-apple="signInWithApple"
    :logout="logout"
    :set-email-login="setEmailLogin"
    :get-code="getCode"
    :before-sign-in="beforeSignIn"
    :back="back"
    @update-form-field="updateFlipnovelFormField"
  />
</template>
